import { useCallback, useEffect, useState } from 'react';
import { api, setToken, type DeviceSummary, type ServerConfig, type Stats } from '../shared/api.ts';
import { formatBytes, formatClock, formatWhen } from '../shared/format.ts';
import { disablePush, enablePush, pushState, type PushState } from './push.ts';

export function Settings({ config }: { config: ServerConfig | null }): React.JSX.Element {
  const [stats, setStats] = useState<Stats | null>(null);
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [pairing, setPairing] = useState<{ code: string; expiresAt: number } | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [notifications, setNotifications] = useState<PushState>('unconfigured');
  const [ttl, setTtl] = useState('24');
  const [libraryTtl, setLibraryTtl] = useState('7');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([api.stats(), api.devices()]);
      setStats(s);
      setDevices(d.devices);
      setTtl(String(s.ttlHours));
      setLibraryTtl(String(s.libraryTtlDays));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void pushState(config?.vapidPublicKey ?? null).then(setNotifications);
  }, [config]);

  useEffect(() => {
    if (!pairing) return;
    const tick = (): void => setRemaining(pairing.expiresAt - Date.now());
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [pairing]);

  const togglePush = async (): Promise<void> => {
    if (!config?.vapidPublicKey) return;
    try {
      if (notifications === 'granted') {
        await disablePush();
        setNotifications('default');
      } else {
        setNotifications(await enablePush(config.vapidPublicKey));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Notification setup failed');
    }
  };

  const saveTtl = async (): Promise<void> => {
    try {
      const result = await api.setTtl({
        ttlHours: Number(ttl),
        libraryTtlDays: Number(libraryTtl),
      });
      setTtl(String(result.ttlHours));
      setLibraryTtl(String(result.libraryTtlDays));
      void reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    }
  };

  return (
    <div className="pane settings">
      {error ? <p className="pane__error mono">{error}</p> : null}

      <section className="settings__block">
        <h2 className="settings__title">Storage</h2>
        {stats ? (
          <dl className="facts mono">
            <div>
              <dt>Library</dt>
              <dd>
                {formatBytes(stats.libraryBytes)} · {stats.fileCount} files
              </dd>
            </div>
            <div>
              <dt>Quarantine</dt>
              <dd>
                {formatBytes(stats.quarantineBytes)} of {formatBytes(stats.ceilingBytes)}
              </dd>
            </div>
            <div>
              <dt>Free on disk</dt>
              <dd>{Number.isFinite(stats.freeBytes) ? formatBytes(stats.freeBytes) : 'unknown'}</dd>
            </div>
          </dl>
        ) : null}
      </section>

      <section className="settings__block">
        <h2 className="settings__title">Retention</h2>
        <p className="settings__hint">
          Unapproved files are deleted after this many hours. Kept files are pruned after this
          many days — the server is a relay with a rolling buffer, not an archive. Set the second
          value to 0 to keep them forever.
        </p>
        <div className="settings__row">
          <label className="settings__field">
            <span className="settings__fieldlabel">Quarantine, hours</span>
            <input
              className="input settings__ttl mono"
              type="number"
              min={1}
              max={720}
              value={ttl}
              onChange={(e) => setTtl(e.target.value)}
            />
          </label>
          <label className="settings__field">
            <span className="settings__fieldlabel">Library, days</span>
            <input
              className="input settings__ttl mono"
              type="number"
              min={0}
              max={3650}
              value={libraryTtl}
              onChange={(e) => setLibraryTtl(e.target.value)}
            />
          </label>
          <button type="button" className="button" onClick={() => void saveTtl()}>
            Save
          </button>
        </div>
      </section>

      <section className="settings__block">
        <h2 className="settings__title">Notifications</h2>
        {config?.vapidPublicKey ? (
          <>
            <p className="settings__hint">
              {notifications === 'granted'
                ? 'This device is subscribed.'
                : notifications === 'denied'
                  ? 'Blocked in browser settings.'
                  : notifications === 'unsupported'
                    ? 'Not available here. On iOS, add Drop to the home screen first.'
                    : 'Not subscribed.'}
            </p>
            <button
              type="button"
              className="button"
              onClick={() => void togglePush()}
              disabled={notifications === 'denied' || notifications === 'unsupported'}
            >
              {notifications === 'granted' ? 'Turn off' : 'Turn on'}
            </button>
          </>
        ) : (
          <p className="settings__hint">Server has no VAPID keys configured.</p>
        )}
      </section>

      <section className="settings__block">
        <h2 className="settings__title">Devices</h2>
        <ul className="devices">
          {devices.map((device) => (
            <li key={device.id} className="devices__item">
              <span className="devices__name">
                {device.name}
                {device.current ? <span className="devices__tag mono"> this device</span> : null}
              </span>
              <span className="devices__meta mono">
                {device.kind} · {device.lastSeen ? formatWhen(device.lastSeen) : 'never seen'}
                {device.hasPush ? ' · push' : ''}
              </span>
              {!device.current ? (
                <button
                  type="button"
                  className="button button--quiet button--danger"
                  onClick={() =>
                    void api.revoke(device.id).then(reload).catch(() => setError('Revoke failed'))
                  }
                >
                  Revoke
                </button>
              ) : null}
            </li>
          ))}
        </ul>

        <div className="settings__row">
          <button
            type="button"
            className="button"
            onClick={() =>
              void api
                .pairingCode()
                .then(setPairing)
                .catch(() => setError('Could not create a pairing code'))
            }
          >
            Pair a new device
          </button>
          {pairing ? (
            <p className="pairing mono">
              <span className="pairing__code">{pairing.code}</span>
              <span className="pairing__clock">{formatClock(remaining)}</span>
            </p>
          ) : null}
        </div>
      </section>

      <section className="settings__block">
        <button
          type="button"
          className="button button--quiet button--danger"
          onClick={() => {
            setToken(null);
            location.reload();
          }}
        >
          Sign this device out
        </button>
      </section>
    </div>
  );
}
